import {
  getApi,
  getApiById,
  patchApi,
  postApi,
  putApi,
} from "../../Shared/Services/RestService";
import {
  CheckoutGateway,
  GatewayName,
  PaymentRequest,
  PaymentResponse,
  PaymentVerifyRequest,
} from "../Types/PaymentType";
import { SubscriptionPlanResponse } from "../Types/SubscriptionPlanType";
import { TaxQuoteResponse } from "../../Admin/Types/TaxConfigType";

export const getAllSubscriptionPlans = () => {
  return getApi<SubscriptionPlanResponse[]>("/subscriptions");
};

export const getSubscriptionPlanById = (id: string ) => {
  return getApiById<SubscriptionPlanResponse | null>("/subscriptions", id);
};

/**
 * The gateways the customer may choose between.
 *
 * <p>The server decides this list, filtering on both "enabled" and "has complete credentials", so a
 * newly configured gateway appears on the picker without any frontend change and a half-configured
 * one never appears at all.
 */
export const getCheckoutGateways = () => {
  return getApi<CheckoutGateway[]>("/payments/gateways");
};

/**
 * The tax-inclusive total of an amount, for display before the customer commits.
 *
 * <p>Asked of the server rather than computed here. Tax is added on top of the plan price, so a
 * locally computed total would be one rounding-rule change away from showing a customer one figure
 * and charging another - and that divergence would only surface as a signature failure at the
 * provider, after they had entered their details.
 */
export const quoteTax = (amount: number) => {
  return postApi<TaxQuoteResponse>("/payments/tax/quote", { amount });
};

export const createPayment = (payload: PaymentRequest) => {
  return postApi<PaymentResponse>("/payments", payload);
};

export const activateTrialPlan = (payload: PaymentRequest) => {
  return postApi<PaymentResponse>("/payments/trail", payload);
};

/**
 * Verifies a checkout return.
 *
 * <p>The gateway and its order id are sent as named fields rather than left inside a provider-shaped
 * body, because the backend looks the payment up by exactly those two. Everything else the provider
 * returned travels in `params`, unverified, for the gateway implementation to check.
 */
export const verifyPayment = (payload: PaymentVerifyRequest) => {
  return postApi<PaymentResponse>("/payments/verify", payload);
};

export const cancelledPayment = (paymentId: string) => {
  return putApi<PaymentResponse>("/payments/cancelled", paymentId);
};

export const updateSubscriptionPlan = (payload: any, id: string) => {
  return putApi<SubscriptionPlanResponse | null>("/subscriptions", id, payload);
};